/** 리포트 — the daily auto report (plan 2026-10-21-aso-auto-report).
 *
 *  /reports opens the newest saved report; /reports/:date shows one. A report
 *  is about its own date. The server builds it at 23:00 KST that day, before
 *  the App Store has published the day (it does the next evening): that
 *  version is 잠정, the day so far without the App Store, and the server
 *  builds it again once the App Store day is in. 다시 만들기 builds it from
 *  the data as it is now. The archive of saved dates sits on the right on a
 *  desktop and is a date select on a phone. */

import { type ReactNode } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Download, FileText, RefreshCw } from 'lucide-react'
import { ApiError, type ByStore, type DailyReport, type ReportListItem, type StoreKey } from '@/api/client'
import { useBuildReport, useCollectRevenue, useReport, useReports, useRevenueStatus } from '@/lib/queries'
import { formatRelative } from '@/lib/time'
import { cn } from '@/lib/utils'
import {
  Bars, Button, IconButton, Disclosure, EmptyState, ErrorState, KeyValue, PageHeader, Segmented, Select, Skeleton, TonePill, buttonClass,
  useQueryParam, useToast, type Column, DataTable,
} from '@/ui'
import { ForecastLine, RevenueColumns, columnsOfDays, columnsOfMonths } from './charts'
import { fmtValue, kstToday, md, num, triggerText, weekday, won } from './format'
import { AppTable, Change, Kpi, KPI_GRID, KPI_LAST, SalesNote, CountryTable, CoverageList, Facts, KeywordMoves, MetricTable, PaymentsTable, ReviewList, Section, ShareBars, StoreBar, StoreSplit } from './parts'

type Tab = 'day' | 'week' | 'month' | 'forecast' | 'basis'
const TABS: { value: Tab; label: string }[] = [
  { value: 'day', label: '전일 대비' },
  { value: 'week', label: '주간' },
  { value: 'month', label: '월간' },
  { value: 'forecast', label: '예상 수익' },
  { value: 'basis', label: '데이터 기준' },
]

export default function Reports() {
  const { date } = useParams()
  const list = useReports()
  const latest = list.data?.[0]?.date
  if (!date) {
    if (list.isLoading) return <Loading />
    if (list.error) return <><PageHeader title="리포트" /><ErrorState title="리포트 목록을 불러오지 못했다" error={list.error} onRetry={() => list.refetch()} /></>
    if (latest) return <Navigate to={`/reports/${latest}`} replace />
    return <NoReports />
  }
  return <DailyView date={date} items={list.data ?? []} />
}

function Loading() {
  return (
    <>
      <PageHeader title="리포트" />
      <Skeleton className="h-40 w-full" />
    </>
  )
}

/** Nothing saved yet: say when reports come and let the first one be built. */
function NoReports() {
  const navigate = useNavigate()
  const build = useBuildReport()
  const toast = useToast()
  const today = kstToday()
  return (
    <>
      <PageHeader title="리포트" />
      <EmptyState
        icon={FileText}
        title="저장된 리포트가 없다"
        description="리포트는 매일 23:00(KST)에 그날 것을 만들고, App Store 판매가 나오는 다음 날 저녁에 다시 만든다. 지금 데이터로 오늘 것을 만들 수도 있다."
        actions={
          <Button
            variant="primary"
            icon={RefreshCw}
            loading={build.isPending}
            onClick={() =>
              build.mutate(today, {
                onSuccess: () => navigate(`/reports/${today}`, { replace: true }),
                onError: (e) => toast.error(`만들지 못했다: ${e.message}`),
              })
            }
          >
            지금 만들기
          </Button>
        }
      />
      <CollectStatus className="mt-6" />
    </>
  )
}

function DailyView({ date, items }: { date: string; items: ReportListItem[] }) {
  const q = useReport(date)
  const build = useBuildReport()
  const toast = useToast()
  const navigate = useNavigate()
  const [tab, setTab] = useQueryParam('tab', 'day')
  const idx = items.findIndex((i) => i.date === date)
  // the list is newest first: the day before is further down
  const older = idx >= 0 ? items[idx + 1]?.date : undefined
  const newer = idx > 0 ? items[idx - 1]?.date : undefined
  const r = q.data

  const rebuild = () =>
    build.mutate(date, {
      onSuccess: () => toast.success('다시 만들었다'),
      onError: (e) => toast.error(`만들지 못했다: ${e.message}`),
    })

  const actions = (
    <>
      <IconButton label="이전 날" icon={ChevronLeft} variant="secondary" disabled={!older} onClick={() => older && navigate(`/reports/${older}`)} />
      <IconButton label="다음 날" icon={ChevronRight} variant="secondary" disabled={!newer} onClick={() => newer && navigate(`/reports/${newer}`)} />
      <Button icon={RefreshCw} loading={build.isPending} disabled={date > kstToday()} onClick={rebuild}>
        다시 만들기
      </Button>
      <a className={buttonClass('secondary')} href={`/api/reports/${date}/md`} download>
        <Download size={16} aria-hidden />
        md 내려받기
      </a>
    </>
  )

  const notBuilt = q.error instanceof ApiError && q.error.status === 404
  return (
    <>
      <PageHeader
        title={`리포트 ${date} (${weekday(date)})`}
        docTitle={`리포트 ${md(date)}`}
        description={r ? headerLine(r) : undefined}
        actions={actions}
      />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_14rem]">
        <div className="min-w-0 space-y-5">
          <ArchiveSelect items={items} date={date} />
          {q.isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : notBuilt ? (
            <EmptyState
              icon={FileText}
              title="이 날짜의 리포트가 없다"
              description="리포트는 매일 23:00(KST)에 그날 것을 만든다. 지금 데이터로 만들 수 있다."
              actions={date <= kstToday() ? <Button variant="primary" icon={RefreshCw} loading={build.isPending} onClick={rebuild}>지금 만들기</Button> : undefined}
            />
          ) : q.error || !r ? (
            <ErrorState title="리포트를 불러오지 못했다" error={q.error} onRetry={() => q.refetch()} />
          ) : (
            <>
              <BasisChips r={r} />
              <Kpis r={r} />
              <Facts items={r.facts} />
              <Segmented label="리포트 구간" options={TABS} value={(TABS.some((t) => t.value === tab) ? tab : 'day') as Tab} onChange={setTab} />
              <TabBody r={r} tab={(TABS.some((t) => t.value === tab) ? tab : 'day') as Tab} />
            </>
          )}
          <CollectStatus />
        </div>
        <Archive items={items} date={date} />
      </div>
    </>
  )
}

function headerLine(r: DailyReport): string {
  const at = r.generated_at ? ` · ${md(r.generated_at.slice(0, 10))} ${new Date(new Date(r.generated_at).getTime() + 9 * 3_600_000).toISOString().slice(11, 16)}` : ''
  return `${triggerText(r.trigger)}${at} · 앱 App Store ${r.app_counts.appstore} · Android ${r.app_counts.android}`
}

function Chip({ label, value }: { label: string; value: ReactNode }) {
  return (
    <li className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-meta">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums">{value}</span>
    </li>
  )
}

/** Which day each figure is as of; a pending report says so first. */
function BasisChips({ r }: { r: DailyReport }) {
  const b = r.basis
  return (
    <ul aria-label="기준일" className="flex flex-wrap items-center gap-1.5">
      {b.sales_pending && (
        <li className="inline-flex items-center gap-1.5">
          <TonePill tone="scheduled" label="잠정 · App Store 집계 전" />
        </li>
      )}
      <Chip label="매출" value={md(b.sales)} />
      {b.sales_pending && b.sales_through && <Chip label="주간·월간" value={`~${md(b.sales_through)}`} />}
      <Chip label="퍼널" value={md(b.funnel)} />
      <Chip label="키워드" value={md(b.keywords)} />
      <Chip label="평점" value={md(b.ratings)} />
      <Chip label="자체 분석" value={md(b.own)} />
      <Chip label="환율" value={md(b.fx_date)} />
    </ul>
  )
}

/** 매출 and 수익 split by store, side by side on a wide screen. */
function SalesAndRevenueBars({ sales, revenue }: { sales: ByStore; revenue: ByStore }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="space-y-1.5">
        <p className="text-table font-medium">매출</p>
        <StoreBar revenue={sales} noun="매출" />
      </div>
      <div className="space-y-1.5">
        <p className="text-table font-medium">수익</p>
        <StoreBar revenue={revenue} />
      </div>
    </div>
  )
}

/** The day's money against the day before; a pending day is the day so far,
 *  so it shows the day before as a figure instead of a change. */
function MoneyVs({ cur, prev, prevDate, pending }: { cur: number | null; prev: number | null; prevDate: string; pending?: boolean }) {
  if (pending) return <div>지금까지 · {md(prevDate)} {won(prev)}</div>
  return (
    <div>
      <Change fmt="krw" cur={cur} prev={prev} /> <span>vs {md(prevDate)}</span>
    </div>
  )
}

function Kpis({ r }: { r: DailyReport }) {
  const k = r.kpis
  const day = md(r.basis.sales)
  const pending = r.basis.sales_pending
  const notIn: StoreKey[] | undefined = pending ? ['appstore'] : undefined
  return (
    <div className="space-y-2">
      <div className={KPI_GRID}>
        <Kpi label={`${day} 매출`} value={won(k.sales.cur)}>
          <MoneyVs cur={k.sales.cur} prev={k.sales.prev} prevDate={r.day.sales_prev} pending={pending} />
          <StoreSplit revenue={k.sales_by_store} noun="매출" pending={notIn} className="flex-col" />
        </Kpi>
        <Kpi label={`${day} 수익`} value={won(k.revenue.cur)}>
          <MoneyVs cur={k.revenue.cur} prev={k.revenue.prev} prevDate={r.day.sales_prev} pending={pending} />
          <StoreSplit revenue={k.revenue_by_store} pending={notIn} className="flex-col" />
        </Kpi>
        <Kpi label="신규 다운로드" value={pending ? '집계 전' : num(k.downloads.cur)}>
          <div>
            {pending ? <span>App Store 판매가 나오면 채운다</span> : <><Change fmt="int" cur={k.downloads.cur} prev={k.downloads.prev} /> <span>App Store</span></>}
          </div>
        </Kpi>
        <Kpi label="키워드 노출점수" value={fmtValue('score', k.score.cur)}>
          <Change fmt="score" cur={k.score.cur} prev={k.score.prev} />
        </Kpi>
        <Kpi label="이번 달 예상" value={won(k.forecast_mid)} className={KPI_LAST}>
          <div>지난달 {won(k.prev_month_total)}</div>
          <Change fmt="krw" cur={k.forecast_mid} prev={k.prev_month_total} />
        </Kpi>
      </div>
      <SalesNote />
    </div>
  )
}

function TabBody({ r, tab }: { r: DailyReport; tab: Tab }) {
  switch (tab) {
    case 'week':
      return <Week r={r} />
    case 'month':
      return <Month r={r} />
    case 'forecast':
      return <Forecast r={r} />
    case 'basis':
      return <Basis r={r} />
    default:
      return <Day r={r} />
  }
}

function Day({ r }: { r: DailyReport }) {
  const d = r.day
  const pending = r.basis.sales_pending
  return (
    <div className="space-y-6">
      <Section title="지표" hint={pending ? `${md(d.sales_cur)} 지금까지 · App Store는 다음 날 저녁 반영` : `${md(d.sales_cur)} vs ${md(d.sales_prev)}`}>
        <MetricTable rows={d.metrics} curLabel={md(d.sales_cur)} prevLabel={md(d.sales_prev)} />
      </Section>
      <Section title="앱별" hint={pending ? '지금까지 · Play·AdMob만' : undefined}>
        <AppTable rows={d.apps} cmp={!pending} />
      </Section>
      <Section title="결제" hint={pending ? 'App Store 결제는 다음 날 저녁 반영' : d.ads_krw > 0 ? `광고 수익 ${won(d.ads_krw)}는 건수에 넣지 않는다` : undefined}>
        <PaymentsTable rows={d.payments} />
      </Section>
      <Section title="새 리뷰">
        <ReviewList rows={d.reviews} />
      </Section>
      <Section title="키워드 이동" hint={d.kw_cur && d.kw_prev ? `${md(d.kw_prev)} → ${md(d.kw_cur)}` : undefined}>
        <div className="grid gap-4 lg:grid-cols-2">
          <KeywordMoves rows={d.keywords_up} dir="up" />
          <KeywordMoves rows={d.keywords_down} dir="down" />
        </div>
      </Section>
    </div>
  )
}

/** A row of a period's headline numbers against the period before. */
function PeriodRows({ rows }: { rows: { label: string; fmt: 'krw' | 'int' | 'pct' | 'score'; cmp: { cur: number | null; prev: number | null }; note?: string }[] }) {
  type Row = (typeof rows)[number]
  const cols: Column<Row>[] = [
    { key: 'label', header: '지표', card: 'title', cell: (x) => <span>{x.label}{x.note && <span className="ml-1.5 text-meta text-muted-foreground">{x.note}</span>}</span> },
    { key: 'cur', header: '이번', align: 'right', cell: (x) => <span className="tabular-nums">{fmtValue(x.fmt, x.cmp.cur)}</span> },
    { key: 'prev', header: '이전', align: 'right', cell: (x) => <span className="tabular-nums text-muted-foreground">{fmtValue(x.fmt, x.cmp.prev)}</span> },
    { key: 'delta', header: '변화', align: 'right', cell: (x) => <Change fmt={x.fmt} cur={x.cmp.cur} prev={x.cmp.prev} /> },
  ]
  return <DataTable columns={cols} rows={rows} rowKey={(x) => x.label} label="기간 지표" />
}

function Week({ r }: { r: DailyReport }) {
  const w = r.week
  return (
    <div className="space-y-6">
      <Section title="최근 7일" hint={`${md(w.from)}–${md(w.to)} vs ${md(w.prev_from)}–${md(w.prev_to)}${throughNote(r)}`}>
        <PeriodRows
          rows={[
            { label: '매출', fmt: 'krw', cmp: w.sales },
            { label: '수익', fmt: 'krw', cmp: w.revenue },
            { label: '신규 다운로드', fmt: 'int', cmp: w.downloads },
            { label: '전환율', fmt: 'pct', cmp: w.cvr, note: w.funnel_from ? `퍼널 ${md(w.funnel_from)}–${md(w.funnel_to)}` : undefined },
            { label: '키워드 점수 변화', fmt: 'score', cmp: w.score_change },
          ]}
        />
        <SalesAndRevenueBars sales={w.sales_by_store} revenue={w.revenue_by_store} />
      </Section>
      <Section title="일별 수익" hint={`${md(w.prev_from)}–${md(w.to)}, 앞 7일은 전주`}>
        <RevenueColumns columns={columnsOfDays(w.days)} label="일별 수익, 스토어별" />
      </Section>
      <Section title="일별 신규 다운로드" hint="앞 7일은 전주">
        <Bars labels={w.days.map((d) => d.date)} outer={w.days.map((d) => d.downloads)} tick={md} title={(i) => `${w.days[i].date} 신규 다운로드 ${num(w.days[i].downloads)}`} label="일별 신규 다운로드" />
      </Section>
      <Section title="앱별">
        <AppTable rows={w.apps} />
      </Section>
      <Section title="국가별">
        <CountryTable rows={w.countries} />
      </Section>
    </div>
  )
}

function Month({ r }: { r: DailyReport }) {
  const m = r.month
  return (
    <div className="space-y-6">
      <Section title="이번 달" hint={`${md(m.from)}–${md(m.to)} vs ${md(m.prev_from)}–${md(m.prev_to)}${throughNote(r)}`}>
        <PeriodRows
          rows={[
            { label: '매출', fmt: 'krw', cmp: m.sales },
            { label: '수익', fmt: 'krw', cmp: m.revenue },
            { label: '신규 다운로드', fmt: 'int', cmp: m.downloads },
            { label: '전환율', fmt: 'pct', cmp: m.cvr, note: m.cvr_prev_from ? `이전 ${md(m.cvr_prev_from)}–${md(m.cvr_prev_to)}` : undefined },
            { label: '키워드 점수 변화', fmt: 'score', cmp: m.score_change },
            { label: '결제 건수', fmt: 'int', cmp: m.payments },
            { label: '활성 사용자', fmt: 'int', cmp: m.active_users },
          ]}
        />
        <SalesAndRevenueBars sales={m.sales_by_store} revenue={m.revenue_by_store} />
      </Section>
      <Section title="월별 수익" hint="빗금은 월말까지 예상">
        <RevenueColumns columns={columnsOfMonths(m.bars)} label="월별 수익, 스토어별" />
      </Section>
      <div className="grid gap-6 lg:grid-cols-3">
        <Section title="앱별 비중">
          <ShareBars items={m.by_app} label="앱별 비중" />
        </Section>
        <Section title="종류별 비중">
          <ShareBars items={m.by_kind} label="종류별 비중" fill="bg-chart-2" />
        </Section>
        <Section title="스토어별 비중">
          <ShareBars items={m.by_store} label="스토어별 비중" fill="bg-chart-3" />
        </Section>
      </div>
    </div>
  )
}

/** On a pending report the periods end where the App Store does. */
function throughNote(r: DailyReport): string {
  return r.basis.sales_pending && r.basis.sales_through ? ` · App Store가 있는 ${md(r.basis.sales_through)}까지` : ''
}

function Forecast({ r }: { r: DailyReport }) {
  const f = r.forecast
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label={`${f.month} 월말 예상`} value={won(f.mid)}>
          <div>{won(f.low)} ~ {won(f.high)}</div>
        </Kpi>
        <Kpi label="지금까지" value={won(f.mtd)}>
          <div>{f.last_day}일 / {f.month_days}일, 남은 {f.remain}일</div>
        </Kpi>
        <Kpi label="다음 달 예상" value={won(f.next_month)}>
          <div>28일 평균 × {f.next_month_days}일</div>
        </Kpi>
        <Kpi label="연 환산" value={won(f.annual)}>
          <div>30일 기준</div>
        </Kpi>
      </div>
      <Section title="월 누적" hint="실선 실적, 점선 예상, 음영 범위">
        <ForecastLine cumulative={f.cumulative} monthDays={f.month_days} lastDay={f.last_day} mtd={f.mtd} low={f.low} mid={f.mid} high={f.high} />
      </Section>
      <Section title="스토어별">
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-1.5">
            <p className="text-table font-medium">지금까지</p>
            <StoreBar revenue={f.mtd_by_store} />
          </div>
          <div className="space-y-1.5">
            <p className="text-table font-medium">월말 예상</p>
            <StoreBar revenue={f.mid_by_store} />
          </div>
        </div>
      </Section>
      <Section title="하루 평균" hint={`지난달(${f.prev_month}) ${won(f.prev_total)}`}>
        <KeyValue items={[{ label: '7일', value: won(f.a7) }, { label: '14일', value: won(f.a14) }, { label: '28일', value: won(f.a28) }]} />
      </Section>
    </div>
  )
}

function Basis({ r }: { r: DailyReport }) {
  const b = r.basis
  return (
    <div className="space-y-6">
      <Section title="기준일">
        <KeyValue
          items={[
            { label: '매출', value: <>{b.sales}{b.sales_pending && <span className="ml-2"><TonePill tone="scheduled" label="App Store 집계 전" /></span>}</> },
            ...(b.sales_through && b.sales_through !== b.sales ? [{ label: '주간·월간·예상', value: `${b.sales_through}까지 (App Store가 있는 마지막 날)` }] : []),
            { label: '퍼널', value: b.funnel ?? '—' },
            { label: '키워드', value: b.keywords ? `${b.keywords} (비교한 키워드 ${num(b.keywords_compared)}개)` : '—' },
            { label: '평점 수', value: b.ratings ?? '—' },
            { label: '자체 분석', value: b.own ?? '—' },
            { label: '환율', value: b.fx_sample.length ? `${b.fx_date} · ${b.fx_sample.map((x) => `1 ${x.currency} = ${won(x.krw)}`).join(' · ')}` : b.fx_date },
          ]}
        />
      </Section>
      <Section title="데이터 범위">
        <CoverageList rows={r.coverage} />
      </Section>
    </div>
  )
}

/** The saved dates, newest first: a column on a desktop. */
function Archive({ items, date }: { items: ReportListItem[]; date: string }) {
  const navigate = useNavigate()
  return (
    <aside aria-label="보관한 리포트" className="hidden lg:block">
      <h2 className="mb-2 text-section font-semibold">보관</h2>
      <ul className="max-h-[70vh] divide-y divide-border overflow-y-auto rounded-lg border border-border bg-card">
        {items.map((i) => (
          <li key={i.date}>
            <button
              type="button"
              aria-current={i.date === date ? 'page' : undefined}
              onClick={() => navigate(`/reports/${i.date}`)}
              className={cn('flex w-full items-baseline justify-between gap-2 px-3 py-2 text-left text-table hover:bg-muted/60', i.date === date && 'bg-muted font-medium')}
            >
              <span className="tabular-nums">{md(i.date)} ({weekday(i.date)})</span>
              <span className="tabular-nums text-muted-foreground">{i.pending ? '잠정' : won(i.revenue_krw)}</span>
            </button>
          </li>
        ))}
      </ul>
    </aside>
  )
}

/** The same list on a phone: a date select. */
function ArchiveSelect({ items, date }: { items: ReportListItem[]; date: string }) {
  const navigate = useNavigate()
  if (items.length === 0) return null
  const shown = items.some((i) => i.date === date) ? items : [{ date, revenue_krw: 0, trigger: '', created_at: '' }, ...items]
  return (
    <Select aria-label="날짜 선택" className="lg:hidden" value={date} onChange={(e) => navigate(`/reports/${e.target.value}`)}>
      {shown.map((i) => (
        <option key={i.date} value={i.date}>
          {i.date} ({weekday(i.date)}){i.pending ? ' · 잠정' : i.revenue_krw ? ` · ${won(i.revenue_krw)}` : ''}
        </option>
      ))}
    </Select>
  )
}

/** Where each revenue source stands, and a button to collect now. */
export function CollectStatus({ className }: { className?: string }) {
  const q = useRevenueStatus()
  const collect = useCollectRevenue()
  const toast = useToast()
  return (
    <Disclosure title="데이터 수집 상태" hint="App Store · Play · AdMob" className={className}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-table text-muted-foreground">수집은 최대 10분 걸린다.</p>
        <Button
          size="sm"
          icon={RefreshCw}
          loading={collect.isPending}
          onClick={() =>
            collect.mutate(undefined, {
              onSuccess: () => toast.success('수집했다'),
              onError: (e) => toast.error(`수집하지 못했다: ${e.message}`),
            })
          }
        >
          지금 수집
        </Button>
      </div>
      {q.isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : q.error ? (
        <ErrorState title="수집 상태를 불러오지 못했다" error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <ul aria-label="수집 상태" className="divide-y divide-border rounded-lg border border-border">
          {(q.data ?? []).map((s) => (
            <li key={s.source} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-3 py-2 text-table">
              <span className="min-w-[6rem] font-medium">{s.label}</span>
              <TonePill tone={s.status === 'ok' ? 'published' : s.status === 'empty' ? 'neutral' : 'failed'} label={s.status === 'ok' ? '정상' : s.status === 'empty' ? '비어 있음' : '문제'} />
              <span className="tabular-nums text-muted-foreground">{s.first ? `${s.first} ~ ${s.last ?? ''}` : '데이터 없음'}</span>
              {s.last_fetch_at && <span className="text-meta text-muted-foreground">마지막 수집 {formatRelative(s.last_fetch_at)}</span>}
              {s.detail && <span className="min-w-0 basis-full break-words text-meta text-muted-foreground">{s.detail}</span>}
            </li>
          ))}
        </ul>
      )}
    </Disclosure>
  )
}
